import { NestAPI, useGetBusiness } from '@marketing-milk/frontend'
import {
  BrandLogo,
  CreatePacketStrategyGroup,
  CreateSprintPacket,
  DeletePacketStrategyGroup,
  MovePacketStrategyAd,
  PacketCustomSection,
  PacketStrategy,
  UpdatePacketCustomSection,
  SprintPacket,
  UpdatePacketStrategyGroup,
  SprintPacketHydrated,
  SprintPacketSurveyStatus,
  UpdateSprintPacket,
} from '@marketing-milk/interfaces'
import { useEffect, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'

type Body<T> = { body: T }

export const useSprintPackets = () => {
  const business = useGetBusiness()
  const endpoint = `businesses/${business.id}/sprint-packets`

  const {
    data: list,
    refetch,
    isLoading,
    isFetched,
    isError,
  } = useQuery(
    ['sprint-packets', business.id],
    async () => {
      const response = await NestAPI.get<Body<SprintPacket[]>>(endpoint)
      return response.data.body
    },
    { retry: false }
  )

  const create = async (dto: CreateSprintPacket): Promise<SprintPacket | undefined> => {
    try {
      const response = await NestAPI.post<Body<SprintPacket>>(endpoint, dto)
      await refetch()
      return response.data.body
    } catch (err) {
      toast.error(`Create action failed: ${err}`)
      return undefined
    }
  }

  return { list, create, refetch, isLoading, isFetched, isError }
}

export const useSprintPacket = (packetID: number | undefined) => {
  const business = useGetBusiness()

  const {
    data: packet,
    refetch,
    isLoading,
    isFetched,
    isError,
  } = useQuery(
    ['sprint-packet', business.id, packetID],
    async () => {
      const response = await NestAPI.get<Body<SprintPacketHydrated>>(
        `businesses/${business.id}/sprint-packets/${packetID}`
      )
      return response.data.body
    },
    {
      retry: false,
      enabled: typeof packetID === 'number' && !Number.isNaN(packetID),
    }
  )

  return { packet, refetch, isLoading, isFetched, isError }
}

// Uploads one brand-logo image to the business's private S3 folder and returns
// its durable object key + a signed preview URL. The key only becomes part of
// the packet when the strategist saves it via `brandStandards`.
export const useUploadBrandLogo = () => {
  const business = useGetBusiness()

  return async (file: File): Promise<BrandLogo> => {
    const formData = new FormData()
    formData.append('image', file)
    const response = await NestAPI.post<Body<BrandLogo>>(
      `businesses/${business.id}/sprint-packets/brand-logos`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return response.data.body
  }
}

// --- Strategy grouping ------------------------------------------------------
//
// Strategy stays OUT of the hydrated packet on purpose. Watching the generation
// status means polling every few seconds, and `getHydrated` assembles
// performance, comments, audiences, survey status and freshly-signed brand-logo
// URLs — re-doing all of that to read one status field. One small parallel
// query avoids two caches and two refresh paths.
//
// The payload carries only creative IDs; the frontend already has the ad
// objects from `useSprint(sprintID)` and joins on them (see `joinStrategy`).
const strategyQueryKey = (businessID: number, packetID: number | undefined) => [
  'sprint-packet-strategy',
  businessID,
  packetID,
]

const strategyEndpoint = (businessID: number, packetID: number | undefined) =>
  `businesses/${businessID}/sprint-packets/${packetID}/strategy`

export const usePacketStrategy = (packetID: number | undefined) => {
  const business = useGetBusiness()
  const queryClient = useQueryClient()
  const previousStatus = useRef<PacketStrategy['status']>()

  const {
    data: strategy,
    refetch,
    isLoading,
  } = useQuery(
    strategyQueryKey(business.id, packetID),
    async () => {
      const response = await NestAPI.get<Body<PacketStrategy>>(
        strategyEndpoint(business.id, packetID)
      )
      return response.data.body
    },
    {
      retry: false,
      enabled: typeof packetID === 'number' && !Number.isNaN(packetID),
      // Generation runs in a queue worker, so a GET alone would never refresh a
      // packet already open in the editor. Poll only while it's running.
      refetchInterval: data => (data?.status === 'generating' ? 3000 : false),
    }
  )

  useEffect(() => {
    const status = strategy?.status
    if (previousStatus.current === 'generating' && status === 'ready') {
      queryClient.invalidateQueries(['sprint-packet', business.id, packetID])
    }
    previousStatus.current = status
  }, [business.id, packetID, queryClient, strategy?.status])

  return { strategy, refetch, isLoading }
}

// Every strategy mutation returns the full refreshed strategy, so the client
// never reconciles locally — it just replaces the query's data.
const useStrategyMutation = <TArgs>(
  packetID: number | undefined,
  request: (businessID: number, args: TArgs) => Promise<PacketStrategy>
) => {
  const business = useGetBusiness()
  const queryClient = useQueryClient()

  return useMutation((args: TArgs) => request(business.id, args), {
    onSuccess: strategy => {
      queryClient.setQueryData(strategyQueryKey(business.id, packetID), strategy)
    },
    onError: err => {
      toast.error(`Save failed: ${err}`)
      queryClient.invalidateQueries(strategyQueryKey(business.id, packetID))
    },
  })
}

export const useRegenerateStrategy = (packetID: number | undefined) =>
  useStrategyMutation<void>(packetID, async businessID => {
    const response = await NestAPI.post<Body<PacketStrategy>>(
      `${strategyEndpoint(businessID, packetID)}/regenerate`
    )
    return response.data.body
  })

export const useUpdateStrategyGroup = (packetID: number | undefined) =>
  useStrategyMutation<{ groupID: number; dto: UpdatePacketStrategyGroup }>(
    packetID,
    async (businessID, { groupID, dto }) => {
      const response = await NestAPI.patch<Body<PacketStrategy>>(
        `${strategyEndpoint(businessID, packetID)}/groups/${groupID}`,
        dto
      )
      return response.data.body
    }
  )

export const useCreateStrategyGroup = (packetID: number | undefined) =>
  useStrategyMutation<CreatePacketStrategyGroup>(packetID, async (businessID, dto) => {
    const response = await NestAPI.post<Body<PacketStrategy>>(
      `${strategyEndpoint(businessID, packetID)}/groups`,
      dto
    )
    return response.data.body
  })

export const useDeleteStrategyGroup = (packetID: number | undefined) =>
  useStrategyMutation<{ groupID: number; dto: DeletePacketStrategyGroup }>(
    packetID,
    async (businessID, { groupID, dto }) => {
      const response = await NestAPI.delete<Body<PacketStrategy>>(
        `${strategyEndpoint(businessID, packetID)}/groups/${groupID}`,
        { data: dto }
      )
      return response.data.body
    }
  )

export const useMoveStrategyAd = (packetID: number | undefined) =>
  useStrategyMutation<{ creativeID: number; dto: MovePacketStrategyAd }>(
    packetID,
    async (businessID, { creativeID, dto }) => {
      const response = await NestAPI.post<Body<PacketStrategy>>(
        `${strategyEndpoint(businessID, packetID)}/ads/${creativeID}/move`,
        dto
      )
      return response.data.body
    }
  )

export const useUnpinStrategyAd = (packetID: number | undefined) =>
  useStrategyMutation<{ creativeID: number }>(packetID, async (businessID, { creativeID }) => {
    const response = await NestAPI.delete<Body<PacketStrategy>>(
      `${strategyEndpoint(businessID, packetID)}/ads/${creativeID}/pin`
    )
    return response.data.body
  })

// --- AE-authored custom slides ----------------------------------------------
//
// Every route returns the full ordered slide list, so success just writes it
// into the hydrated packet's `customSections` — no local reconciliation.
//
// Both queries are invalidated on every mutation: the list page counts
// unreviewed slides per row off `useSprintPackets`, so without the second
// invalidation that badge goes stale the moment an AE reviews a slide.
const customSectionsEndpoint = (businessID: number, packetID: number | undefined) =>
  `businesses/${businessID}/sprint-packets/${packetID}/custom-sections`

const useCustomSectionMutation = <TArgs>(
  packetID: number | undefined,
  request: (businessID: number, args: TArgs) => Promise<PacketCustomSection[]>
) => {
  const business = useGetBusiness()
  const queryClient = useQueryClient()
  const packetQueryKey = ['sprint-packet', business.id, packetID]

  return useMutation((args: TArgs) => request(business.id, args), {
    onSuccess: customSections => {
      const cached = queryClient.getQueryData<SprintPacketHydrated>(packetQueryKey)
      if (cached) {
        queryClient.setQueryData<SprintPacketHydrated>(packetQueryKey, {
          ...cached,
          customSections,
        })
      }
      queryClient.invalidateQueries(packetQueryKey)
      queryClient.invalidateQueries(['sprint-packets', business.id])
    },
    onError: err => {
      toast.error(`Save failed: ${err}`)
      queryClient.invalidateQueries(packetQueryKey)
    },
  })
}

export const useCreateCustomSection = (packetID: number | undefined) =>
  useCustomSectionMutation<void>(packetID, async businessID => {
    const response = await NestAPI.post<Body<PacketCustomSection[]>>(
      customSectionsEndpoint(businessID, packetID)
    )
    return response.data.body
  })

export const useUpdateCustomSection = (packetID: number | undefined) =>
  useCustomSectionMutation<{ id: string; dto: UpdatePacketCustomSection }>(
    packetID,
    async (businessID, { id, dto }) => {
      const response = await NestAPI.patch<Body<PacketCustomSection[]>>(
        `${customSectionsEndpoint(businessID, packetID)}/${id}`,
        dto
      )
      return response.data.body
    }
  )

export const useDeleteCustomSection = (packetID: number | undefined) =>
  useCustomSectionMutation<{ id: string }>(packetID, async (businessID, { id }) => {
    const response = await NestAPI.delete<Body<PacketCustomSection[]>>(
      `${customSectionsEndpoint(businessID, packetID)}/${id}`
    )
    return response.data.body
  })

export const useMoveCustomSection = (packetID: number | undefined) =>
  useCustomSectionMutation<{ id: string; direction: 'up' | 'down' }>(
    packetID,
    async (businessID, { id, direction }) => {
      const response = await NestAPI.post<Body<PacketCustomSection[]>>(
        `${customSectionsEndpoint(businessID, packetID)}/${id}/move`,
        { direction }
      )
      return response.data.body
    }
  )

// --- Salesforce survey link -------------------------------------------------
//
// Re-runs the SOQL and re-snapshots the packet, returning the new status. The
// success path writes it straight into the hydrated packet's `.survey` and stops
// there: unlike the custom-section mutations there is nothing else to refresh —
// the list payload carries no survey status, and invalidating the packet key we
// just wrote would refetch for nothing.
//
// No `onError` toast: a failed refresh renders inline on the card, beside the QR
// it deliberately left untouched.
export const useRefreshSurveyLink = (packetID: number | undefined) => {
  const business = useGetBusiness()
  const queryClient = useQueryClient()
  const packetQueryKey = ['sprint-packet', business.id, packetID]

  return useMutation(
    async (): Promise<SprintPacketSurveyStatus> => {
      const response = await NestAPI.post<Body<SprintPacketSurveyStatus>>(
        `businesses/${business.id}/sprint-packets/${packetID}/survey/refresh`
      )
      return response.data.body
    },
    {
      onSuccess: survey => {
        const cached = queryClient.getQueryData<SprintPacketHydrated>(packetQueryKey)
        if (cached) {
          queryClient.setQueryData<SprintPacketHydrated>(packetQueryKey, { ...cached, survey })
        }
      },
    }
  )
}

export const useUpdateSprintPacket = (packetID: number | undefined) => {
  const business = useGetBusiness()
  const queryClient = useQueryClient()

  return useMutation(
    async (dto: UpdateSprintPacket): Promise<SprintPacket> => {
      const response = await NestAPI.patch<Body<SprintPacket>>(
        `businesses/${business.id}/sprint-packets/${packetID}`,
        dto
      )
      return response.data.body
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['sprint-packet', business.id, packetID])
      },
      onError: err => {
        toast.error(`Save failed: ${err}`)
      },
    }
  )
}
